import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LockKey, Check, Warning } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForceChangePasswordModal({ lang = 'fr' }) {
  const { user, fetchUser } = useAuth();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isAr = lang === 'ar';
  const open = user && user.must_change_password === true;

  useEffect(() => { setError(''); }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 8) {
      setError(isAr ? 'كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل' : 'Le nouveau mot de passe doit faire au moins 8 caractères');
      return;
    }
    if (!(/[a-zA-Z]/.test(newPwd) && /\d/.test(newPwd))) {
      setError(isAr ? 'يجب أن تحتوي على حرف ورقم' : 'Doit contenir au moins une lettre et un chiffre');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError(isAr ? 'كلمتا المرور غير متطابقتين' : 'Les deux mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPwd,
        new_password: newPwd,
      }, { withCredentials: true });
      // Refresh user state (must_change_password should be false now)
      await fetchUser();
    } catch (err) {
      setError(err.response?.data?.detail || (isAr ? 'خطأ' : 'Erreur'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="force-change-password-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Warning size={22} weight="fill" />
            </div>
            <div>
              <h2 className="font-bold text-lg">
                {isAr ? 'تغيير كلمة المرور إلزامي' : 'Changement de mot de passe obligatoire'}
              </h2>
              <p className="text-xs opacity-90 mt-0.5">
                {isAr ? 'كلمة المرور المؤقتة منتهية. أنشئ كلمة مرور جديدة الآن.' : 'Votre mot de passe temporaire doit être changé maintenant.'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1.5 uppercase tracking-wider">
              {isAr ? 'كلمة المرور المؤقتة' : 'Mot de passe temporaire reçu'} *
            </label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)}
                data-testid="force-old-pwd"
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none font-mono"
                placeholder="••••••••" required autoFocus />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1.5 uppercase tracking-wider">
              {isAr ? 'كلمة المرور الجديدة' : 'Nouveau mot de passe'} *
            </label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                data-testid="force-new-pwd"
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                placeholder="••••••••" required />
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              {isAr ? 'على الأقل 8 أحرف، حرف ورقم' : 'Min. 8 caractères, contenant au moins une lettre et un chiffre'}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1.5 uppercase tracking-wider">
              {isAr ? 'تأكيد كلمة المرور' : 'Confirmation'} *
            </label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                data-testid="force-confirm-pwd"
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                placeholder="••••••••" required />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm" data-testid="force-error">{error}</div>
          )}

          <button type="submit" disabled={submitting}
            data-testid="force-submit-btn"
            className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-bold text-sm shadow-md disabled:opacity-50 flex items-center justify-center gap-2">
            <Check size={18} weight="bold" />
            {submitting ? (isAr ? 'جاري التغيير...' : 'Changement…') : (isAr ? 'تغيير كلمة المرور' : 'Changer mon mot de passe')}
          </button>

          <p className="text-[11px] text-center text-zinc-500">
            {isAr ? 'لا يمكنك الوصول إلى التطبيق قبل تغيير كلمة المرور.' : 'Vous ne pourrez pas accéder à l\'application avant d\'avoir changé votre mot de passe.'}
          </p>
        </form>
      </div>
    </div>
  );
}
